
import React, { useState, useMemo, useEffect } from 'react';
import { Tagihan, SortField, SortDirection, DetailBulan } from './types';

const formatRupiah = (n: number) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n);
const formatNumber = (n: number) => new Intl.NumberFormat('id-ID').format(n);
const formatBulanTahun = (bulan: number, tahun: number) =>
  `${new Intl.DateTimeFormat('id-ID', { month: 'long' }).format(new Date(tahun, bulan - 1, 1))} ${tahun}`;

const KECAMATAN = ["Semua Kecamatan", "Bolangitang Barat", "Bolangitang Timur", "Kaidipang", "Bintauna", "Sangkub", "Pinogaluman"];
const STATUS = ["Semua Status", "Belum Bayar", "Menunggak", "Lunas", "Nonaktif"];

export default function DaftarTagihan() {
    const [initialData, setInitialData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [kecamatan, setKecamatan] = useState('Semua Kecamatan');
    const [status, setStatus] = useState('Semua Status');
    const [sortField, setSortField] = useState<SortField>('total_jumlah');
    const [sortDir, setSortDir] = useState<SortDirection>('DESC');
    const [selected, setSelected] = useState<Tagihan | null>(null);
    const [paymentTarget, setPaymentTarget] = useState<Tagihan | null>(null);
    const [showMap, setShowMap] = useState(false);
    const [page, setPage] = useState(1);
    const pageSize = 10;

    const [detailBulan, setDetailBulan] = useState<DetailBulan[]>([]);
    const [detailLoading, setDetailLoading] = useState(false);

    const [selectedBulan, setSelectedBulan] = useState<Record<string, boolean>>({});
    const [metodeBayar, setMetodeBayar] = useState<'tunai' | 'transfer'>('tunai');
    const [noHp, setNoHp] = useState('');

    const filtered = useMemo(() => {
        let res = [...initialData];
        // search sudah di-handle server, jadi jangan filter lagi di sini
        if (kecamatan!== 'Semua Kecamatan') res = res.filter(t => t.kecamatan === kecamatan);
        if (status!== 'Semua Status') res = res.filter(t => (t.status || t.status_aktif) === status);

        res.sort((a,b) => {
        let av: any = a[sortField];
        let bv: any = b[sortField];
        if (typeof av === 'string') { av = av.toLowerCase(); bv = (bv||'').toLowerCase(); }
        if (sortDir === 'ASC') return av > bv? 1 : -1;
        return av < bv? 1 : -1;
        });
        return res;
    }, [initialData, kecamatan, status, sortField, sortDir]);

  const paginated = useMemo(() => {
    const start = (page-1)*pageSize;
    return filtered.slice(start, start+pageSize);
  }, [filtered, page]);

  const stats = useMemo(() => {
    const total = filtered.reduce((s,t) => s + (t.total_jumlah||0), 0);
    const denda = filtered.reduce((s,t) => s + (t.denda||0), 0);
    return { total, denda, count: filtered.length };
  }, [filtered]);

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'ASC' ? 'DESC' : 'ASC');
    else { setSortField(field); setSortDir('DESC'); }
  };

    // ===== FETCH YANG BENAR - TANPA VARIABLE LUAR =====
    const fetchDataTagihan = async (keyword: string) => {
        const apiUrl = process.env.REACT_APP_API_URL_V2;
        const apiKey = process.env.REACT_APP_API_KEY;
        if (!apiUrl) throw new Error("REACT_APP_API_URL_V2 tidak ter-set");

        const myHeaders = new Headers();
        if (apiKey) myHeaders.append("Authorization", "Bearer " + apiKey);
        myHeaders.append("Content-Type", "application/json");

        // INI INTINYA
        const clean = keyword.trim();
        const endpoint = clean
            ? `${apiUrl}/hublang/tunggakan/v2/${encodeURIComponent(clean)}`
            : `${apiUrl}/hublang/tunggakan/v2/all`;

        console.log("Fetching:", endpoint);

        const response = await fetch(endpoint, {
            method: "GET",
            headers: myHeaders,
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(`HTTP ${response.status}: ${text.slice(0,200)}`);
        }

        const result = await response.json();
        const rawData = result.data?.data?? result.data?? [];
        if (!Array.isArray(rawData)) return [];

        return rawData.map((item: any, idx: number) => ({
            id: item.id?.toString()?? idx.toString(),
            no_sambung: item.no_sambung || '-',
            status_aktif: item.status_aktif || item.status || '-',
            status: item.status || item.status_aktif || 'Belum Bayar',
            nama_pelanggan: item.nama_pelanggan || '-',
            alamat: item.alamat || '-',
            kecamatan: item.kecamatan || '-',
            kelurahan: item.kelurahan || '-',
            golongan: item.golongan || '-',
            jumlah_tagihan: Number(item.jumlah_tagihan || 0),
            denda: Number(item.denda || 0),
            total_jumlah: Number(item.total_jumlah || 0),
            jumlah_bulan: Number(item.jumlah_bulan || 0),
            no_hp: item.no_hp || '-',
            lat: Number(item.lat || 0),
            lng: Number(item.lng || 0),
            detail_bulan: item.detail_bulan || []
        }));
    }

    const fetchDetailTagihan = async (noSambung: string) => {
        const apiUrl = process.env.REACT_APP_API_URL_V2;
        const apiKey = process.env.REACT_APP_API_KEY;
        if (!apiUrl) throw new Error("API URL tidak set");

        const myHeaders = new Headers();
        if (apiKey) myHeaders.append("Authorization", "Bearer " + apiKey);
        myHeaders.append("Content-Type", "application/json");

        const cleanNo = noSambung.trim();
        const endpoint = `${apiUrl}/hublang/tunggakan/v2/detail/${encodeURIComponent(cleanNo)}`;
        console.log("Fetching detail:", endpoint);

        const res = await fetch(endpoint, { method: "GET", headers: myHeaders });
        if (!res.ok) {
            const txt = await res.text();
            throw new Error(`HTTP ${res.status}: ${txt.slice(0,200)}`);
        }
        const json = await res.json();
        // handle kemungkinan { data: [...] } atau { data: { data: [...] } }
        const raw = json.data?.data?? json.data?? json?? [];
        return Array.isArray(raw)? raw as DetailBulan[] : [];
    };  

    const tampilkanData = async () => {
        setLoading(true);
        setInitialData([]);
        try {
            // kirim state search ke fetch
            const data = await fetchDataTagihan(search);
            setInitialData(data);
            setPage(1);
            if (data.length === 0) {
            alert(`Data tidak ditemukan untuk "${search || 'semua'}"`);
            }
        } catch (err: any) {
            alert("Gagal load data: " + err.message);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!selected) {
            setDetailBulan([]);
            return;
        }
        const loadDetail = async () => {
            setDetailLoading(true);
            try {
                const data = await fetchDetailTagihan(selected.no_sambung);
                // kalau API kosong, fallback ke detail_bulan yang dari list all
                setDetailBulan(data.length > 0? data : (selected.detail_bulan || []));

                console.log("Detail");
                console.log(data)
            } catch (e) {
                console.error("Gagal fetch detail:", e);
                setDetailBulan(selected.detail_bulan || []);
            } finally {
                setDetailLoading(false);
            }
        };
        loadDetail();
    }, [selected]);

    const openPayment = (t: Tagihan) => {
        setPaymentTarget(t);
        setSelectedBulan({});
        setNoHp(t.no_hp);
        setMetodeBayar('tunai');
    };

    const detailStats = useMemo(() => {
        const tagihan = detailBulan.reduce((s,d) => s + (d.tagihan||0), 0);
        const denda = detailBulan.reduce((s,d) => s + (d.denda||0), 0);
        const total = detailBulan.reduce((s,d) => s + (d.total||0), 0);
        return { tagihan, denda, total, count: detailBulan.length };
    }, [detailBulan]);

  const toggleBulan = (periode: string) => {
    setSelectedBulan(prev => ({ ...prev, [periode]: !prev[periode] }));
  };

  const paymentTotal = useMemo(() => {
    if (!paymentTarget) return 0;
    return paymentTarget.detail_bulan.filter(d => selectedBulan[d.periode]).reduce((s,d) => s + d.total, 0);
  }, [paymentTarget, selectedBulan]);

  return (
    <div className="min-h-screen bg-[#f0f4fc] text-slate-800">
      <div className="max-w-[1400px] mx-auto px-6 pt-8 pb-4">
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div className="flex gap-3 items-center">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center text-red-600 shadow-sm">📄</div>
            <div>
              <h1 className="text-[26px] font-semibold text-[#1a2634] leading-none">Daftar Tagihan</h1>
              <p className="text-sm text-slate-500 mt-1">PDAM Tirta Moolango • {formatNumber(stats.count)} pelanggan terfilter</p>
            </div>
          </div>
        </div>

        {/* <div className="bg-white rounded-2xl p-5 mt-5 border border-slate-100 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
            <div className="md:col-span-4">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">Cari Pelanggan</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">⌕</span>
                <input value={search} onChange={e=> setSearch(e.target.value)} placeholder="Nama, No Sambung, Alamat..." className="w-full h-11 pl-9 pr-4 rounded-xl border-[1.5px] border-slate-100 bg-[#fafbfc] focus:bg-white focus:border-[#1e88e5] focus:ring-4 focus:ring-blue-50 outline-none text-sm transition-all"/>
              </div>
            </div>
            {/* <div className="md:col-span-3">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">Kecamatan</label>
              <select value={kecamatan} onChange={e=>{setKecamatan(e.target.value); setPage(1)}} className="w-full h-11 px-3 rounded-xl border-[1.5px] border-slate-100 bg-[#fafbfc] focus:bg-white focus:border-[#1e88e5] outline-none text-sm">
                {KECAMATAN.map(k=><option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">Status</label>
              <select value={status} onChange={e=>{setStatus(e.target.value); setPage(1)}} className="w-full h-11 px-3 rounded-xl border-[1.5px] border-slate-100 bg-[#fafbfc] focus:bg-white focus:border-[#1e88e5] outline-none text-sm">
                {STATUS.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="md:col-span-3 flex gap-2">
              <button type="button" onClick={tampilkanData} disabled={loading} className="flex-1 h-11 rounded-xl bg-[#1e88e5] hover:bg-[#1565c0] disabled:bg-slate-300 text-white flex items-center justify-center gap-2 text-sm font-semibold shadow-[0_8px_16px_rgba(30,136,229,0.24)] transition-all">
                {loading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    Loading...
                  </>
                ) : (
                  "Tampilkan Data"
                )}
              </button>
            </div>
          </div>
        </div> */}

        <div className="bg-white rounded-2xl p-5 mt-5 border border-slate-100 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-8">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">Cari Pelanggan</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">⌕</span>
                <input value={search} onChange={e=> setSearch(e.target.value)} placeholder="Nama, No Sambung, Alamat..." className="w-full h-11 pl-9 pr-4 rounded-xl border-[1.5px] border-slate-100 bg-[#fafbfc] focus:bg-white focus:border-[#1e88e5] focus:ring-4 focus:ring-blue-50 outline-none text-sm transition-all"/>
              </div>
            </div>
            {/* <div className="md:col-span-3">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">Kecamatan</label>
              <select value={kecamatan} onChange={e=>{setKecamatan(e.target.value); setPage(1)}} className="w-full h-11 px-3 rounded-xl border-[1.5px] border-slate-100 bg-[#fafbfc] focus:bg-white focus:border-[#1e88e5] outline-none text-sm">
                {KECAMATAN.map(k=><option key={k} value={k}>{k}</option>)}
              </select>
            </div> */}
            {/* <div className="md:col-span-2">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">Status</label>
              <select value={status} onChange={e=>{setStatus(e.target.value); setPage(1)}} className="w-full h-11 px-3 rounded-xl border-[1.5px] border-slate-100 bg-[#fafbfc] focus:bg-white focus:border-[#1e88e5] outline-none text-sm">
                {STATUS.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div> */}
            <div className="md:col-span-4 flex justify-end">
              <button type="button" onClick={tampilkanData} disabled={loading} className="w-full md:w-auto md:min-w-[180px] h-11 px-5 rounded-xl bg-[#1e88e5] hover:bg-[#1565c0] disabled:bg-slate-300 text-white flex items-center justify-center gap-2 text-sm font-semibold shadow-[0_8px_16px_rgba(30,136,229,0.24)] transition-all">
                {loading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    Loading...
                  </>
                ) : (
                  "Tampilkan Data"
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl mt-5 border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#fafbfc] border-b border-slate-100">
                <tr className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <th className="text-left py-3.5 px-5 whitespace-nowrap">Pelanggan / Gol</th>
                  <th className="text-left py-3.5 px-3 whitespace-nowrap">Alamat / Kecamatan</th>
                  <th className="text-right py-3.5 px-3 whitespace-nowrap cursor-pointer hover:text-slate-700" onClick={()=>handleSort('total_jumlah')}>Tagihan <span>{sortField==='total_jumlah' ? (sortDir==='DESC' ? '↓' : '↑') : ''}</span></th>
                  {/* <th className="text-center py-3.5 px-5 whitespace-nowrap">Aksi</th> */}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {paginated.map((t) => (
                  <tr key={t.id} className="hover:bg-[#f8faff] transition-colors group" onClick={()=>setSelected(t)}>
                    <td className="py-4 px-5">
                      <div className="flex flex-col">
                        <span className="font-semibold text-[13px] text-slate-900 group-hover:text-[#1e88e5]">{t.nama_pelanggan}</span>
                        <span className="text-xs text-slate-500 font-mono mt-0.5">{t.no_sambung} • <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 inline-block mt-1">{t.golongan?.split(' - ')[0] || t.golongan}</span></span>
                        <span className="text-xs text-slate-500 font-mono mt-0.5"><span className={`text-white text-[11px] font-bold px-2 py-1 rounded-lg ${t.status_aktif?.toLowerCase() === 'aktif' ? 'bg-blue-500' : t.status_aktif?.toLowerCase() === 'segel' ? 'bg-yellow-500' : t.status_aktif?.toLowerCase() === 'cabut meter' ? 'bg-red-500' : 'bg-slate-500'}`}>{t.status_aktif}</span></span>
                      </div>
                    </td>
                    <td className="py-4 px-3">
                        <span className="text-[12px] font-medium">{t.alamat}</span>
                        <div className="text-[11px] text-slate-500">{t.kecamatan}</div>
                    </td>
                    <td className="py-4 px-3 text-right">
                        { t.status_aktif !== "Aktif" ? "-" : 
                            (
                                <>
                                    {
                                        t.jumlah_bulan === 0 ? 
                                        (
                                            <>
                                                <span className="bg-blue-500 text-white text-[11px] font-bold px-2 py-1 rounded-lg">Lunas</span>
                                            </>
                                        )
                                        :
                                        (
                                            <>
                                                <span className="bg-red-500 text-white text-[11px] font-bold px-2 py-1 rounded-lg"> {t.jumlah_bulan} bln</span>
                                                <div className="text-[13px] font-bold text-slate-900">{formatRupiah(t.total_jumlah)}</div>
                                                {t.denda > 0 && <div className="text-[11px] text-red-500">Denda {formatRupiah(t.denda)}</div>}
                                            </>
                                        )
                                    }
                                </>
                            )
                        
                        }
                        
                    </td>
                    {/* <td className="py-4 px-5">
                      <div className="flex gap-1.5 justify-center">
                        <button onClick={()=>setSelected(t)} className="w-8 h-8 rounded-lg bg-slate-50 hover:bg-[#1e88e5] hover:text-white flex items-center justify-center text-slate-500 transition-all text-xs">👁</button>
                      </div>
                    </td> */}
                  </tr>
                ))}
                {paginated.length===0 && !loading && (
                  <tr><td colSpan={4} className="py-16 text-center text-slate-400 text-sm">
                    {initialData.length === 0 ? "Klik Tampilkan Data" : "Tidak ada data yang cocok dengan filter"}
                  </td></tr>
                )}
                {loading && (
                  <tr><td colSpan={4} className="py-16 text-center text-slate-400 text-sm">Memuat data...</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center px-5 py-4 border-t border-slate-100 bg-[#fafbfc]">
            <div className="text-xs text-slate-500">Menampilkan {(page-1)*pageSize+1}-{Math.min(page*pageSize, filtered.length)} dari {filtered.length} data (total fetch: {initialData.length})</div>
            <div className="flex gap-1">
              <button disabled={page===1} onClick={()=>setPage(p=>Math.max(1,p-1))} className="w-8 h-8 rounded-lg border border-slate-200 bg-white disabled:opacity-40 text-sm">‹</button>
              {Array.from({length: Math.ceil(filtered.length/pageSize)}).slice(0,5).map((_,i)=>(
                <button key={i} onClick={()=>setPage(i+1)} className={`w-8 h-8 rounded-lg text-sm font-medium ${page===i+1 ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200'}`}>{i+1}</button>
              ))}
              <button disabled={page>=Math.ceil(filtered.length/pageSize)} onClick={()=>setPage(p=>p+1)} className="w-8 h-8 rounded-lg border border-slate-200 bg-white disabled:opacity-40 text-sm">›</button>
            </div>
          </div>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-[20px] w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-lg">Detail Tagihan</h3>
                <p className="text-xs text-slate-500 font-mono mt-0.5">{selected.id} • {selected.no_sambung}</p>
              </div>
              <button onClick={()=>setSelected(null)} className="w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center">✕</button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-[#f8faff] rounded-xl p-4 border border-blue-50">
                  <div className="text-[11px] font-semibold uppercase text-slate-500">Pelanggan</div>
                  <div className="font-semibold mt-1">{selected.nama_pelanggan}</div>
                  <div className="text-sm text-slate-600 mt-1">{selected.alamat}</div>
                  <div className="text-xs text-slate-500 mt-1">{selected.kecamatan} • {selected.kelurahan}</div>
                </div>
                <div className="bg-[#f8faff] rounded-xl p-4 border border-blue-50">
                  <div className="text-[11px] font-semibold uppercase text-slate-500">Info Langganan</div>
                  <div className="text-sm mt-1"><span className="text-slate-500">Gol:</span> {selected.golongan}</div>
                  <div className="text-sm mt-1"><span className="text-slate-500">HP:</span> {selected.no_hp}</div>
                  <div className="text-sm mt-1"><span className="text-slate-500">Status:</span> {selected.status_aktif}</div>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden border border-slate-100">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold">
                    <tr>
                        <th className="text-left py-2.5 px-4">Periode</th>
                        <th className="text-right px-4">Tagihan</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {detailLoading ?  (
                        <tr><td colSpan={4} className="py-12 text-center text-slate-400">Memuat rincian {selected.no_sambung}...</td></tr>
                    ) : detailBulan.length === 0 ? (
                        <tr><td colSpan={4} className="py-12 text-center text-slate-400">Tidak ada rincian tunggakan</td></tr>
                    ) : (detailBulan || []).map((d:any)=>(
                        <tr key={d.periode}>
                            <td className="py-2.5 px-4 font-medium">{formatBulanTahun(d.bulan, d.tahun)}</td>
                            <td className="py-2.5 px-4 text-right">{formatRupiah(d.total)} <br /><span className='text-red-500'>{formatRupiah(d.denda)}</span></td>
                        </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-900 text-white font-semibold">
                    <tr>
                        <td className="py-3 px-4">Total {selected.jumlah_bulan} Bulan</td>
                        <td className="py-3 px-4 text-right">{formatRupiah(selected.total_jumlah)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-[#fafbfc]">
              <button onClick={()=>setSelected(null)} className="px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-sm font-semibold">Tutup</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
